<template>
  <div class="main_div" :key="componentKey">
    <div style="height: auto">
      <n-space vertical>
        <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
          <n-space item-style="display: flex;" align="center">
            <div style="font-size: 20px; font-weight: bold">개인 결제 관리</div>
          </n-space>
          <div class="content_delimiter_line"></div>

          <n-space vertical style="margin-top: 10px">
            <n-space align="baseline" item-style="display: flex;">
              <n-form-item label="검색" style="min-width: 350px" label-placement="left">
                <n-input-group>
                  <n-select v-model:value="searchType" :options="searchOptions" style="width: 120px" />
                  <n-input v-model:value="searchValue" placeholder="검색어를 입력하세요" clearable />
                </n-input-group>
              </n-form-item>
              <n-form-item style="min-width: 300px" label-placement="left"> </n-form-item>
              <n-form-item style="margin-bottom: 0">
                <n-button type="primary" @click="wrappedRefreshOrders"> 조회 </n-button>
              </n-form-item>
            </n-space>
          </n-space>

          <Loading v-show="loading" />
          <div>
            <div v-if="listLoading" class="loading-indicator">개인 신청 목록을 불러오는 중...</div>
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="!listLoading && !error">
              <div class="table-container">
                <n-data-table
                  v-if="orders.length > 0"
                  :columns="columns"
                  :data="orders"
                  :pagination="false"
                  :bordered="true"
                />
                <p v-else>등록된 신청이 없습니다.</p>
              </div>
              <div v-if="orders.length > 0" class="pagination-controls" style="margin-top: 15px; text-align: center">
                <n-button @click="wrappedGoToPreviousPage" :disabled="currentPage === 1" style="margin-right: 8px">
                  이전 페이지
                </n-button>
                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
              </div>
            </div>
          </div>
        </NCard>

        <!-- 신청 상세 정보 모달 -->
        <n-modal
          v-model:show="showOrderDetailsModal"
          preset="card"
          style="width: 800px"
          title="신청 상세 정보"
          @after-leave="onOrderDetailsModalClose"
        >
          <div v-if="selectedOrder" class="order-details-content">
            <n-descriptions label-placement="left" bordered :column="2" size="small">
              <n-descriptions-item label="신청자">{{ selectedOrder.client.uid }}</n-descriptions-item>
              <n-descriptions-item label="수신자 이메일">{{
                selectedOrder.receiver_email || "검사 시 입력"
              }}</n-descriptions-item>
              <n-descriptions-item label="금액"
                >{{ Number(selectedOrder.totalPrice || 0).toLocaleString() }} 원</n-descriptions-item
              >
              <n-descriptions-item label="상태">{{
                common.getPaidStatusString(selectedOrder.is_paid)
              }}</n-descriptions-item>
              <n-descriptions-item label="생성일">
                {{ common.getYYYYMMDD2(new Date(Number(selectedOrder.createdDate))) }}
              </n-descriptions-item>
              <n-descriptions-item label="결제 방법" :span="2">
                {{
                  selectedOrder.payment_method?.type === "bank_deposit"
                    ? "계좌이체"
                    : selectedOrder.payment_method?.type || "N/A"
                }}
              </n-descriptions-item>
              <n-descriptions-item
                v-if="selectedOrder.payment_method?.type === 'bank_deposit'"
                label="결제 계좌"
                :span="2"
              >
                {{ selectedOrder.payment_method.bank_name }}
              </n-descriptions-item>
              <n-descriptions-item
                v-if="selectedOrder.payment_method?.type === 'bank_deposit'"
                label="입금자명"
                :span="2"
              >
                {{ selectedOrder.payment_method.deposit_name }}
              </n-descriptions-item>
              <n-descriptions-item
                v-if="selectedOrder.payment_method?.type === 'bank_deposit'"
                label="입금예정일"
                :span="2"
              >
                {{ selectedOrder.payment_method.bank_date }}
              </n-descriptions-item>
            </n-descriptions>

            <h4 style="margin-top: 20px; margin-bottom: 10px">신청 항목</h4>
            <n-data-table
              :columns="orderItemColumns"
              :data="selectedOrder.products"
              :pagination="false"
              :bordered="true"
              size="small"
            />

            <div style="text-align: right; margin-top: 20px">
              <n-button @click="closeOrderDetailsModal" type="tertiary" style="margin-right: 10px"> 닫기 </n-button>
            </div>
          </div>
          <div v-else class="loading-indicator">상세 정보를 불러오는 중...</div>
        </n-modal>
      </n-space>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h, computed } from "vue";
import {
  NSpace,
  NCard,
  NButton,
  NDataTable,
  NModal,
  NSelect,
  NDescriptions,
  NDescriptionsItem,
  useMessage,
  useNotification,
  useDialog,
  NFormItem,
  NInputGroup,
  NInput,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import { useRouter } from "vue-router";
import { getCallable, makeCallApiParam } from "@/config/env";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import { useAuth } from "@/composables/useAuth";
import { QueryInfo, ChangeStatus } from "@/model/base";
import { Order } from "@/model/order";
import { useOrderApi } from "@/composables/useOrderForm";
import { usePagination } from "@/composables/usePagination";
import { useActionDialog } from "@/composables/useDialog";
const { show: showActionDialog } = useActionDialog();
const { deleteOrder } = useOrderApi();

const { loading } = useAuth(); // 전역 로딩 상태
const router = useRouter();
const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const pagination = ref<QueryInfo>({
  pageSize: 25,
  lastEvaluatedKey: null,
  previousLastEvaluatedKeys: [],
});

const componentKey = ref(0);
const showOrderDetailsModal = ref(false);
const selectedOrder = ref<Order | null>(null);
const searchValue = ref<string | null>(null);
const searchType = ref("guardian.name");

const getUnpaiedPersonalOrdersCallable = getCallable("getUnpaiedPersonalOrders");
const getCallApiCallable = getCallable("callApi");
const changeOrderStatusFunc = "changeOrderStatus";
const setOrderPaymentCallable = getCallable("setOrderPayment");
const isPaying = ref(false);

const searchOptions = [
  // { label: "피검자", value: "products[0].projects[0].examinees[0].name" },
  { label: "보호자", value: "guardian.name" },
];

const {
  paginatedItems: orders,
  listLoading,
  error,
  currentPage,
  hasNextPage,
  goToNextPage: composableGoToNextPage,
  goToPreviousPage: composableGoToPreviousPage,
  refreshData: composableRefreshOrders,
} = usePagination<Order>(
  async (params) => getUnpaiedPersonalOrdersCallable(params),
  25 // 페이지 크기
);
async function wrappedRefreshOrders() {
  loading.value = true;

  const searchParams: { searchKey?: string; searchValue?: string } = {};

  if (searchValue.value && searchValue.value.trim() !== "") {
    searchParams.searchKey = searchType.value;
    searchParams.searchValue = searchValue.value.trim();
  }

  await composableRefreshOrders(searchParams);
  loading.value = false;
}
const columns = [
  {
    title: "피검자",
    key: "products.0.projects.0.examinees.0.name",
    resizable: true,
  },
  { title: "보호자", key: "guardian.name", resizable: true },
  {
    title: "신청일",
    key: "createdDate",
    resizable: true,
    render: (row: Order) => {
      const timestamp = Number(row.createdDate);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp);
        return common.getYYYYMMDD2(date);
      }
      return row.createdDate || "N/A";
    },
  },
  {
    title: "금액",
    key: "totalPrice",
    resizable: true,
    render: (row: Order) => `${Number(row.totalPrice || 0).toLocaleString()}원`,
  },
  {
    title: "상태",
    key: "is_paid",
    resizable: true,
    render: (row: Order) => `${common.getPaidStatusString(row.is_paid)}`,
  },
  {
    title: "",
    key: "actions",
    render(row: Order) {
      const buttons: any = [];
      buttons.push(
        h(
          NButton,
          {
            strong: true,
            tertiary: true,
            size: "small",
            onClick: () => displayOrderDetails(row),
          },
          { default: () => "상세" }
        )
      );
      buttons.push(
        h(
          NButton,
          {
            style: "margin-left: 10px",
            strong: true,
            tertiary: true,
            size: "small",
            onClick: () => confirmOrderPaid(row),
          },
          { default: () => "결제 확정" }
        )
      );
      return buttons;
    },
  },
];

const orderItemColumns = [{ title: "상품명", key: "name", resizable: true }];
async function wrappedGoToNextPage() {
  if (hasNextPage.value) {
    await composableGoToNextPage();
  }
}

async function wrappedGoToPreviousPage() {
  if (currentPage.value > 1) {
    await composableGoToPreviousPage();
  }
}
async function displayOrderDetails(order: Order) {
  selectedOrder.value = order;
  showOrderDetailsModal.value = true;
}

function closeOrderDetailsModal() {
  showOrderDetailsModal.value = false;
}

function onOrderDetailsModalClose() {
  showOrderDetailsModal.value = false;
  selectedOrder.value = null;
}

async function confirmOrder(order: Order) {
  if (order.status == constant.orderStatus.PENDING) {
    showActionDialog({
      title: "확인",
      content: "신청 확정도 진행 하시겠습니까?",
      positiveText: "네",
      negativeText: "아니요",
      positiveButtonProps: { type: "error" },
      confirmButtonLoadingText: "처리 중...",
      onConfirm: async () => {
        const data: ChangeStatus = {
          SK: order.SK,
          status: constant.orderStatus.READY,
        };
        const payload: {
          status: constant.orderStatus;
          type: constant.orderType;
          data: ChangeStatus;
        } = {
          status: constant.orderStatus.READY,
          type: constant.orderType.INDIVIDUAL,
          data: data,
        };
        const result = await getCallApiCallable(makeCallApiParam(changeOrderStatusFunc, payload));
        if (common.evalFunctionResult(result)) {
          message.success(`신청 확정이 완료되었습니다.`);

          await wrappedRefreshOrders();
        } else {
          message.success(common.getResultError(result) || `신청 확정중 오류가 발생했습니다.`);
        }
      },
      onCancel: async () => {
        await wrappedRefreshOrders();
      },
    });
  }
}

async function confirmOrderPaid(order: Order) {
  const orderSK: string = order.SK!;
  showActionDialog({
    title: "확인",
    content: "결제(입금확인) 처리를 하시겠습니까?",
    positiveText: "네",
    negativeText: "아니요",
    positiveButtonProps: { type: "error" },
    confirmButtonLoadingText: "처리 중...",
    onConfirm: async () => {
      isPaying.value = true;
      message.loading("결제(입금확인) 처리 중...");
      try {
        const result = await setOrderPaymentCallable({
          SK: orderSK,
          pay_type: constant.paymentType.BANK_TRANSFER,
        });
        if (common.evalFunctionResult(result)) {
          message.success("결제(입금확인) 처리가 완료되었습니다.");
          confirmOrder(order);
        } else {
          throw new Error(common.getResultError(result) || "결제(입금확인) 처리 중 오류가 발생했습니다.");
        }
      } catch (error: any) {
        console.error("결제(입금확인) 처리 API 호출 실패:", error);
        message.error(common.getResultError(error) || "결제(입금확인) 처리 중 오류가 발생했습니다.");
      } finally {
        isPaying.value = false;
      }
    },
  });
}

onMounted(() => {
  wrappedRefreshOrders(); // 초기 데이터 로드
});
</script>

<style scope></style>
